import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Autocomplete,
  Tabs,
  Tab,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { appointmentsAPI, horsesAPI, vetsAPI, appointmentTypesAPI } from '../services/api';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { isCurrentHorse } from '../utils/horseStatus';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { cardSx } from '../theme';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'cancelled':
      return 'error';
    case 'no_show':
      return 'warning';
    default:
      return 'default';
  }
};

const toDateTimeInputString = (value) => {
  if (!value) return '';
  if (value instanceof Date) {
    return value.toISOString().slice(0, 16);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.length >= 16) {
      return trimmed.slice(0, 16);
    }
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 16);
    }
    return trimmed;
  }
  return '';
};

const toDateInputString = (value) => {
  if (!value) return '';
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.length >= 10) {
      return trimmed.slice(0, 10);
    }
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 10);
    }
    return trimmed;
  }
  return '';
};

const formatDateTimeLocal = (value) => toDateTimeInputString(value);
const formatDateInput = (value) => toDateInputString(value);

const formatDateTimeDisplay = (value) => formatDateTime(value) ?? '-';
const formatDateDisplay = (value) => formatDate(value) ?? '-';

const toIsoStringOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return value.toISOString().slice(0, 16);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.length >= 16) {
      return trimmed.slice(0, 16);
    }
    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 16);
    }
    return trimmed;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 16);
};

const formatCurrencyDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(number)) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(number);
};

const createInitialFormData = () => ({
  horse_id: '',
  appointment_type_id: '',
  appointment_type: '',
  appointment_date: '',
  vet_id: '',
  vet_name: '',
  vet_clinic: '',
  status: 'scheduled',
  completed_date: '',
  diagnosis: '',
  treatment: '',
  medications: '',
  follow_up_required: false,
  follow_up_date: '',
  cost: '',
  paid: false,
  notes: '',
});

const mapAppointmentToFormState = (appointment) => {
  const base = createInitialFormData();
  if (!appointment) return base;

  return {
    ...base,
    horse_id: appointment.horse_id || '',
    appointment_type_id: appointment.appointment_type_id || '',
    appointment_type: appointment.appointment_type || '',
    appointment_date: formatDateTimeLocal(appointment.appointment_date),
    vet_id: appointment.vet_id || '',
    vet_name: appointment.vet_name || '',
    vet_clinic: appointment.vet_clinic || '',
    status: appointment.status || base.status,
    completed_date: formatDateTimeLocal(appointment.completed_date),
    diagnosis: appointment.diagnosis || '',
    treatment: appointment.treatment || '',
    medications: appointment.medications || '',
    follow_up_required: Boolean(appointment.follow_up_required),
    follow_up_date: formatDateInput(appointment.follow_up_date),
    cost:
      appointment.cost !== null && appointment.cost !== undefined
        ? String(appointment.cost)
        : base.cost,
    paid: Boolean(appointment.paid),
    notes: appointment.notes || '',
  };
};

const AppointmentsPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [activeTab, setActiveTab] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [horses, setHorses] = useState([]);
  const [vets, setVets] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [openVetDialog, setOpenVetDialog] = useState(false);
  const [vetFormData, setVetFormData] = useState({
    clinic_name: '',
    vet_name: '',
    phone: '',
    email: '',
    emergency_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    specialties: '',
    notes: '',
  });
  const [openAppointmentTypeDialog, setOpenAppointmentTypeDialog] = useState(false);
  const [appointmentTypeFormData, setAppointmentTypeFormData] = useState({
    name: '',
    description: '',
    default_duration_minutes: 60,
  });

  const [formData, setFormData] = useState(() => createInitialFormData());

  const horseOptions = useMemo(() => {
    const current = horses.filter(isCurrentHorse);
    if (formData.horse_id && !current.some((h) => h.id === formData.horse_id)) {
      const selected = horses.find((h) => h.id === formData.horse_id);
      if (selected) return [...current, selected];
    }
    return current;
  }, [horses, formData.horse_id]);

  useEffect(() => {
    loadData();
  }, []);

  useRefreshOnFocus(() => loadData());

  const loadData = async () => {
    setLoadError('');
    try {

      const [appointmentsRes, horsesRes, vetsRes, appointmentTypesRes] = await Promise.all([
        appointmentsAPI.getVetAppointments(),
        horsesAPI.getAll(),
        vetsAPI.getAll(),
        appointmentTypesAPI.getAll(),
      ]);
      setAppointments(toArray(appointmentsRes.data, 'appointments'));
      setHorses(toArray(horsesRes.data, 'horses'));
      setVets(toArray(vetsRes.data, 'vets'));
      setAppointmentTypes(toArray(appointmentTypesRes.data, 'appointment types'));
      
    } catch (error) {
      console.error('Error loading data:', error);
      console.error('Error details:', error.response?.data);
      // A 401 is not handled here. The axios interceptor in services/api.js already
      // refreshes the token and retries, and logs out and redirects when the session
      // is genuinely dead — clearing storage here as well only wiped a live session
      // without redirecting anywhere.
      //
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no
      // appointments".
      setLoadError(error?.response?.data?.error || 'Unable to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (appointment = null) => {
    fields.reset();
    if (appointment) {
      setEditingAppointment(appointment);
      setFormData(mapAppointmentToFormState(appointment));
    } else {
      setEditingAppointment(null);
      setFormData(createInitialFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAppointment(null);
    setFormData(createInitialFormData());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const valid = fields.validate({
      horse_id: !formData.horse_id && 'Horse is required',
      appointment_type_id: !formData.appointment_type_id && 'Appointment type is required',
      appointment_date: !formData.appointment_date && 'Date and time are required',
    });
    if (!valid) return;
    try {
      const costValue =
        formData.cost !== '' && formData.cost !== null
          ? parseFloat(formData.cost)
          : null;

      const payload = {
        horse_id: formData.horse_id || null,
        appointment_type_id: formData.appointment_type_id || null,
        appointment_type: formData.appointment_type || undefined,
        appointment_date: toIsoStringOrNull(formData.appointment_date),
        vet_id: formData.vet_id || null,
        vet_name: formData.vet_name || null,
        vet_clinic: formData.vet_clinic || null,
        status: formData.status,
        completed_date: toIsoStringOrNull(formData.completed_date),
        diagnosis: formData.diagnosis || null,
        treatment: formData.treatment || null,
        medications: formData.medications || null,
        follow_up_required: Boolean(formData.follow_up_required),
        follow_up_date:
          formData.follow_up_required && formData.follow_up_date
            ? formData.follow_up_date
            : null,
        cost: Number.isNaN(costValue) ? null : costValue,
        paid: Boolean(formData.paid),
        notes: formData.notes || null,
      };

      if (editingAppointment) {
        await appointmentsAPI.updateVetAppointment(editingAppointment.id, payload);
      } else {
        await appointmentsAPI.createVetAppointment(payload);
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving appointment:', error);
      notify.error(errorMessage(error, 'Error saving appointment'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await appointmentsAPI.deleteVetAppointment(deleteTarget);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      notify.error(errorMessage(error, 'Error deleting appointment'));
    }
  };


  const handleOpenVetDialog = () => {
    setVetFormData({
      clinic_name: '',
      vet_name: '',
      phone: '',
      email: '',
      emergency_phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      specialties: '',
      notes: '',
    });
    setOpenVetDialog(true);
  };

  const handleCloseVetDialog = () => {
    setOpenVetDialog(false);
  };

  const handleVetInputChange = (e) => {
    const { name, value } = e.target;
    setVetFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateVetFromDialog = async () => {
    try {
      const response = await vetsAPI.create(vetFormData);
      const newVet = response.data;
      setVets(prev => [...prev, newVet]);
      setFormData(prev => ({
        ...prev,
        vet_id: newVet.id,
        vet_name: newVet.vet_name,
        vet_clinic: newVet.clinic_name
      }));
      handleCloseVetDialog();
    } catch (error) {
      console.error('Error creating vet:', error);
      notify.error(errorMessage(error, 'Error creating vet'));
    }
  };

  const handleOpenAppointmentTypeDialog = () => {
    setAppointmentTypeFormData({
      name: '',
      description: '',
      default_duration_minutes: 60,
    });
    setOpenAppointmentTypeDialog(true);
  };

  const handleCloseAppointmentTypeDialog = () => {
    setOpenAppointmentTypeDialog(false);
  };

  const handleAppointmentTypeInputChange = (e) => {
    const { name, value } = e.target;
    setAppointmentTypeFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAppointmentTypeFromDialog = async () => {
    try {
      const response = await appointmentTypesAPI.create(appointmentTypeFormData);
      const newType = response.data;
      setAppointmentTypes(prev => [...prev, newType]);
      fields.clear('appointment_type_id');
      setFormData(prev => ({
        ...prev,
        appointment_type_id: newType.id,
        appointment_type: newType.name,
      }));
      handleCloseAppointmentTypeDialog();
    } catch (error) {
      console.error('Error creating appointment type:', error);
      notify.error(errorMessage(error, 'Error creating appointment type'));
    }
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Appointments</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Appointment
          </Button>
        </Box>
      </Box>

      <Paper sx={{ ...cardSx, mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Vet Appointments" />
          <Tab label="Pregnancy Shots" />
        </Tabs>
      </Paper>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
      {activeTab === 0 && (
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader>
            <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell>Horse</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Vet</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Follow-Up</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={8} />}
              {appointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(appointment) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(appointment.id), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>{appointment.horse_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{appointment.appointment_type || '-'}</Typography>
                    {appointment.appointment_type_description && (
                      <Typography variant="caption" color="text.secondary">
                        {appointment.appointment_type_description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTimeDisplay(appointment.appointment_date)}
                    </Typography>
                    {appointment.completed_date && (
                      <Typography variant="caption" color="text.secondary">
                        Completed: {formatDateTimeDisplay(appointment.completed_date)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {appointment.vet_name || '-'}
                    </Typography>
                    {appointment.vet_clinic && (
                      <Typography variant="caption" color="text.secondary">
                        {appointment.vet_clinic}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={appointment.status || 'Unknown'}
                      size="small"
                      color={getStatusColor(appointment.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={Boolean(appointment.follow_up_required) ? 'Required' : 'Not Required'}
                      size="small"
                      color={Boolean(appointment.follow_up_required) ? 'warning' : 'default'}
                      variant={Boolean(appointment.follow_up_required) ? 'filled' : 'outlined'}
                    />
                    {appointment.follow_up_date && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Due: {formatDateDisplay(appointment.follow_up_date)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrencyDisplay(appointment.cost)}
                    {Boolean(appointment.paid) && (
                      <Chip
                        label="Paid"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 1 && (
        <Paper sx={{ ...cardSx, flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          <Typography color="textSecondary">
            Pregnancy shots tracking will be displayed here
          </Typography>
        </Paper>
      )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
        </DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={horseOptions}
                getOptionLabel={(option) => option.name || option.registered_name || `Horse #${option.id}`}
                filterOptions={createPrefixFilter((option) => option.name || option.registered_name || `Horse #${option.id}`)}
                value={horseOptions.find((h) => h.id === formData.horse_id) || null}
                onChange={(e, newValue) => {
                  fields.clear('horse_id');
                  setFormData((prev) => ({ ...prev, horse_id: newValue?.id || '' }));
                }}
                renderInput={(params) => (
                  <TextField size="small" {...params} label="Horse" required {...fields.props('horse_id')} />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  options={appointmentTypes}
                  getOptionLabel={(option) => option.name}
                  filterOptions={createPrefixFilter((option) => option.name)}
                  value={appointmentTypes.find((t) => t.id === formData.appointment_type_id) || null}
                  noOptionsText={`No appointment types found (${appointmentTypes.length} loaded)`}
                  onChange={(e, newValue) => {
                    fields.clear('appointment_type_id');
                    setFormData((prev) => ({
                      ...prev,
                      appointment_type_id: newValue?.id || '',
                      appointment_type: newValue?.name || '',
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField size="small" {...params} label="Appointment Type" required {...fields.props('appointment_type_id')} />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {option.name}
                          </Typography>
                          {option.description && (
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  sx={{ flexGrow: 1 }}
                />
                <IconButton
                  onClick={handleOpenAppointmentTypeDialog}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    width: 56,
                    height: 56,
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50',
                      borderColor: 'primary.dark',
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="appointment_date"
                label="Appointment Date & Time"
                type="datetime-local"
                value={formData.appointment_date}
                onChange={handleInputChange}
                {...fields.props('appointment_date')}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                fullWidth
                disableClearable
                options={STATUS_OPTIONS}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                filterOptions={createPrefixFilter((option) => option.label)}
                value={STATUS_OPTIONS.find((option) => option.value === formData.status) || null}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, status: newValue?.value || '' }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Status" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="completed_date"
                label="Completed Date & Time"
                type="datetime-local"
                value={formData.completed_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  options={vets}
                  getOptionLabel={(option) => `${option.clinic_name}${option.vet_name ? ` - ${option.vet_name}` : ''}`}
                  filterOptions={createPrefixFilter((option) => `${option.clinic_name}${option.vet_name ? ` - ${option.vet_name}` : ''}`)}
                  value={vets.find((v) => v.id === formData.vet_id) || null}
                  noOptionsText={`No veterinarians found (${vets.length} loaded)`}
                  onChange={(e, newValue) => {
                    setFormData((prev) => ({ 
                      ...prev, 
                      vet_id: newValue?.id || '', 
                      vet_name: newValue?.vet_name || '',
                      vet_clinic: newValue?.clinic_name || prev.vet_clinic
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField size="small" {...params} label="Veterinarian" />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {option.clinic_name}
                          </Typography>
                          {option.vet_name && (
                            <Typography variant="body2" color="text.secondary">
                              {option.vet_name}
                            </Typography>
                          )}
                          {option.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {option.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  sx={{ flexGrow: 1 }}
                />
                <IconButton
                  onClick={handleOpenVetDialog}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    width: 56,
                    height: 56,
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50',
                      borderColor: 'primary.dark',
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.follow_up_required)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        follow_up_required: e.target.checked,
                        follow_up_date: e.target.checked ? prev.follow_up_date : '',
                      }))
                    }
                  />
                }
                label="Follow-up required"
              />
              <TextField
                size="small"
                sx={{ mt: 1 }}
                name="follow_up_date"
                label="Follow-up Date"
                type="date"
                value={formData.follow_up_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={!formData.follow_up_required}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.paid)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paid: e.target.checked,
                      }))
                    }
                  />
                }
                label="Paid"
              />
              <TextField
                size="small"
                sx={{ mt: 1 }}
                name="cost"
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="diagnosis"
                label="Diagnosis"
                value={formData.diagnosis}
                onChange={handleInputChange}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="treatment"
                label="Treatment"
                value={formData.treatment}
                onChange={handleInputChange}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="medications"
                label="Medications"
                value={formData.medications}
                onChange={handleInputChange}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange}
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAppointment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Vet Dialog */}
      <Dialog open={openVetDialog} onClose={handleCloseVetDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add New Veterinarian</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="clinic_name"
                  label="Clinic Name"
                  value={vetFormData.clinic_name}
                  onChange={handleVetInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="vet_name"
                  label="Veterinarian Name"
                  value={vetFormData.vet_name}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="phone"
                  label="Phone"
                  value={vetFormData.phone}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={vetFormData.email}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="emergency_phone"
                  label="Emergency Phone"
                  value={vetFormData.emergency_phone}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="address"
                  label="Address"
                  value={vetFormData.address}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="city"
                  label="City"
                  value={vetFormData.city}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(option) => option.name}
                  filterOptions={createPrefixFilter((option) => option.name)}
                  value={US_STATES.find(state => state.code === vetFormData.state) || null}
                  onChange={(event, newValue) => {
                    setVetFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      label="State"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        {option.code} - {option.name}
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="zip_code"
                  label="Zip Code"
                  value={vetFormData.zip_code}
                  onChange={handleVetInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="specialties"
                  label="Specialties"
                  value={vetFormData.specialties}
                  onChange={handleVetInputChange}
                  fullWidth
                  placeholder="e.g., Equine Surgery, Internal Medicine, Emergency Care"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={vetFormData.notes}
                  onChange={handleVetInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVetDialog}>Cancel</Button>
          <Button onClick={handleCreateVetFromDialog} variant="contained">
            Add Veterinarian
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAppointmentTypeDialog} onClose={handleCloseAppointmentTypeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Appointment Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              name="name"
              label="Name"
              value={appointmentTypeFormData.name}
              onChange={handleAppointmentTypeInputChange}
              fullWidth
              required
            />
            <TextField
              size="small"
              name="description"
              label="Description"
              value={appointmentTypeFormData.description}
              onChange={handleAppointmentTypeInputChange}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              size="small"
              name="default_duration_minutes"
              label="Default Duration (minutes)"
              type="number"
              value={appointmentTypeFormData.default_duration_minutes}
              onChange={handleAppointmentTypeInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAppointmentTypeDialog}>Cancel</Button>
          <Button
            onClick={handleCreateAppointmentTypeFromDialog}
            variant="contained"
            disabled={!appointmentTypeFormData.name.trim()}
          >
            Add Appointment Type
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Appointment"
        message="Permanently delete this appointment? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default AppointmentsPage;

